<template>
  <div class="max-w-7xl mx-auto sm:px-2 lg:px-8 pb-12 hidden">
    <router-link
      to="/social"
      class="relative inline-flex items-center my-4 py-2 leading-5 font-medium text-lg font-semibold
         transition ease-in-out duration-150 text-lightFont hover:text-darkFont uppercase"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="fill-current h-6 w-6 mr-2 "
        viewBox="0 0 256 256"
      >
        <path
          d="M208,72H128V32a8,8,0,0,0-13.66-5.66l-96,96a8,8,0,0,0,0,11.32l96,96A8,8,0,0,0,128,224V184h80a16,16,0,0,0,16-16V88A16,16,0,0,0,208,72Zm0,96H120a8,8,0,0,0-8,8v28.69L35.31,128,112,51.31V80a8,8,0,0,0,8,8h88Z"
        ></path>
      </svg>
      Back to Topics
    </router-link>
    <div v-if="loading">
      <submission-spinner></submission-spinner>
    </div>
    <div
      class="bg-white border-vectric border-4  overflow-hidden sm:rounded-t-2xl sm:rounded-br-2xl mt-2 flex flex-col"
      v-if="topic && !loading"
    >
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
        <div class="flex flex row">
          <avatar :user-display="topic.userDisplay"></avatar>
          <div class="ml-2">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              {{ topic.topic }}
            </h3>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
              Started by {{ topic.userDisplay }} on {{ postDate }}
            </p>
          </div>
        </div>
      </div>
      <vue-markdown
        :source="topic.body"
        :anchorAttributes="{ target: '_blank' }"
        class="px-4 py-5 sm:px-6"
      ></vue-markdown>
    </div>
    <div class="ml-12 w-1 h-4 bg-vectric"></div>

    <DynamicScroller
      v-if="messages.length > 0"
      :items="messages"
      :min-item-size="120"
      class="bg-white border-vectric border-4  overflow-y-auto sm:rounded-t-2xl sm:rounded-br-2xl "
      ref="messageChat"
      v-on:visible="handleScrollerVisible"
    >
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[item.body]"
          :data-index="index"
          v-bind:class="{ 'border-t border-gray-200': index > 0 }"
        >
          <topic-message
            :message="item"
            v-on:remove-message="handleRemoveMessage"
            v-on:replying-to="handleReplyingTo"
            v-on:scroll-to-message="handleScrollToMessage"
          ></topic-message>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
    <div :class="{ 'mt-4': messages.length > 0 }">
      <topic-response-box
        ref="topicResponseBox"
        :id="id"
        v-if="loggedIn"
        :replying-to="replyingTo"
        v-on:cancel-reply="handleCancelReply"
        v-on:message-sent="handleSentMessage"
      ></topic-response-box>
      <if-can-take-part></if-can-take-part>
    </div>
  </div>
</template>

<script>
window.location.href = "/";
import { DynamicScroller } from "vue-virtual-scroller";
import { DynamicScrollerItem } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { topicsCollection } from "../utils/firebase";
import TopicMessage from "../components/TopicMessage";
import TopicResponseBox from "../components/TopicResponseBox";
import Avatar from "../components/Avatar";
import SubmissionSpinner from "../components/SubmissionSpinner";
import { mapGetters } from "vuex";
import { removeTopicComment } from "../utils/functions";
import IfCanTakePart from "../components/IfCanTakePart";
import VueMarkdown from "vue-markdown";

export default {
  name: "Topic",
  components: {
    IfCanTakePart,
    SubmissionSpinner,
    Avatar,
    TopicResponseBox,
    TopicMessage,
    DynamicScroller,
    DynamicScrollerItem,
    VueMarkdown
  },
  data() {
    return {
      loading: true,
      id: null,
      topic: null,
      messagesRef: null,
      messages: [],
      messagesUnsubscribe: null,
      replyingTo: null
    };
  },
  computed: {
    ...mapGetters(["loggedIn"]),
    postDate() {
      if (this.topic) {
        return this.topic.timestamp.toDate().toLocaleString();
      } else {
        return "";
      }
    }
  },
  created() {
    this.id = this.$route.params.id;
    this.messagesRef = topicsCollection.doc(this.id).collection("messages");

    //Get the data
    topicsCollection
      .doc(this.id)
      .get()
      .then(doc => (this.topic = doc.data()));

    // Get messages
    this.loading = true;
    this.messages = [];
    this.messagesUnsubscribe = this.messagesRef
      .orderBy("timestamp", "asc")
      .onSnapshot(querySnapshot => {
        this.loading = false;
        querySnapshot.docChanges().forEach(change => {
          const doc = change.doc;
          if (change.type === "added") {
            this.messages.push({ id: doc.id, ...doc.data() });
          }

          if (change.type === "modified") {
            const index = this.messages.findIndex(
              item => item.id === change.doc.id
            );
            this.messages.splice(index, 1, { id: doc.id, ...doc.data() });
          }

          if (change.type === "removed") {
            const index = this.messages.findIndex(
              item => item.id === change.doc.id
            );
            this.messages.splice(index, 1);
          }
        });
        this.$nextTick(() => this.scrollToBottom());
      });
  },
  beforeDestroy() {
    if (this.messagesUnsubscribe) {
      this.messagesUnsubscribe();
    }
  },
  methods: {
    //
    // Remove a message if you are admin
    //
    handleRemoveMessage(messageID) {
      this.$store.dispatch("setDeletedMessageId", messageID);
      removeTopicComment(this.id, messageID);
    },
    handleScrollerVisible() {
      this.scrollToBottom();
    },
    scrollToBottom() {
      this.$refs.messageChat.scrollToBottom();
    },
    handleReplyingTo(message) {
      this.replyingTo = message;
      if (this.$refs.topicResponseBox) {
        this.$refs.topicResponseBox.focusInput();
      }
    },
    handleCancelReply() {
      this.replyingTo = null;
    },
    handleSentMessage() {
      this.replyingTo = null;
    },
    handleScrollToMessage(message) {
      const index = this.messages.findIndex(x => x.id === message.id);
      if (index != -1) {
        this.$refs.messageChat.scrollToItem(index);
      } else {
        console.warn("Unable to find item with id" + message.id);
      }
    }
  }
};
</script>

<style scoped>
.emoji-mart {
  position: absolute;
  top: -400px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
